import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { FeedbackBuffer } from '../feedback-buffer.js';

// Shared-rig contract: forbidden. There is one light in this picture, and it
// is red, and it is the size of a pixel. A deck wash would be the brightest
// thing on screen.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'signal',
  family: 'vector',
  danceability: 0.45,
  energyLevel: 0.40,
  bpmMin: 70,
  bpmMax: 150,
  movement: 0.35,
  tonality: 0.75,
  prefers: { gridStrength: [0.50, 1.0] },
  enabled: true
};

// Signal: a radio mast on a hill at night, seen from far away, whose top
// sends a ring of signal into the sky on every beat.
//
// ALMOST NOTHING ON SCREEN. A silhouette, a red light, faint rings. The
// sobriety is the subject, and every number below was chosen against that:
// a ring is a quarter-opacity line of a pixel and a half, the beacon is a
// dot, the sky is nearly black. What makes it worth watching is that the
// mast carries TWO antennas a few percent of the frame apart: their ring
// families are offset, and where two rings cross the light doubles. Every
// same-beat pair crosses on the perpendicular bisector of the two antennas,
// and near it the two rings run almost together for a while, so the sky
// above the mast carries a standing bright band, the fringe, that no single
// antenna could draw. A THIRD antenna, lower on the mast and off to one
// side, comes and goes on the phrases: its bisectors with the other two are
// tilted, and the pattern of bands changes.
//
// GEOMETRY IS IN FRAME UNITS. y runs -1..1 over the frame's height, x runs
// -A..A with A the aspect; the whole picture is one vertical plane a fixed
// distance in front of the camera, sized so that plane fills the frame
// exactly. Nothing here needs depth; the layers are ordered by renderOrder
// with the depth test off. The kiosk's 3.2:1 panels and a 16:9 TV are the
// same picture with more or less sky either side.

// --- the frame ---------------------------------------------------------------
const PLANE_D = 10;                    // the picture's plane, in front of the camera
const PYLON_X_FRAC = 0.35;             // of the frame's width, from the left
const HILL_SUMMIT_Y = -0.34;           // the hill's top: the bottom third
const HILL_FOOT_Y = -0.62;             // and its flanks at the frame's edges
const HILL_COLS = 160;                 // silhouette resolution
const PYLON_H = 0.80;                  // 40% of the frame's height (which is 2)
const PYLON_BASE_HALF_W = 0.062;
const PYLON_TOP_HALF_W = 0.014;
const PYLON_PANELS = 9;
const MAST_H = 0.075;                  // the thin mast above the lattice
const ANTENNA_SEP_FRAC = 0.03;         // the outer antennas, 3% of the width apart
const ANTENNA_STUB = 0.045;
const TREES = 9;
const SAFE_FLOOR = 0.55;               // the brief's floor for this chunk
// The picture overruns the frame by a fifth on each side: the hill's strip
// and the rings' plane both reach past the edges, so nothing stops on a
// vertical line whatever the aspect turns out to be.
const OVERRUN = 1.20;

// --- rings -------------------------------------------------------------------
const RING_SEGMENTS = 96;
const RING_REACH_BEATS = 6;            // a ring reaches the frame's edge in six beats
const RING_LIFE_BEATS = 8;             // and is gone in eight
const RING_OPACITY = 0.22;             // at birth, falling to 0 over the life
const RING_WIDTH_PX = 1.5;
// Analytic anti-aliasing: the quad is drawn AA_PX wider on each side and the
// fragment ramps its alpha to zero over that band, from the across-the-line
// coordinate LineMaterial already carries. Exact at any angle, and the quad
// never falls under two pixels, where a fat line breaks up.
const AA_PX = 1.0;
const RING_LOW_SWAY = 0.15;            // bands.low: +-15% on the expansion speed
const RING_TINT_SAT = 0.4;
const RING_TINT_LIGHT = 0.66;          // light, never white
// The rings' buffer (tier 2), at FULL resolution: a line a pixel and a
// half wide cannot be drawn in a half-res target (it would be three on
// screen, or break up under two buffer pixels), and the buffer's whole
// point is the composite, not the economy. Where two rings add, the
// composite lifts the sum by this much again, so a crossing reads as more
// than twice one ring - the fringe is what one is meant to see.
const BUFFER_SCALE = 1.0;
const FRINGE_GAIN = 0.9;
// The drop's pulse: thick, bright, across the whole sky in two beats.
const PULSE_WIDTH_PX = 6.0;
const PULSE_BEATS = 2;
const PULSE_GAIN = 0.9;

// --- the beacon: StrobeWall's filament ---------------------------------------
const HEAT_RATE = 22.0;                // ~45 ms to 63%
const COOL_RATE = 5.5;                 // ~180 ms
const BEACON_ON_BARS = 0.125;          // lit for half a beat of a four-beat bar
const BEACON_R = 0.014;                // the lamp, in frame units
const BEACON_HALO_R = 0.09;
const LATTICE_LIT_SPAN = 0.22;         // the top of the lattice the lamp reaches, of its height
const LATTICE_LIT_GAIN = 0.28;         // weakly: the lamp is the warm point, not the mast

// --- the road ----------------------------------------------------------------
const HEADLIGHT_PASSES = [0.25, 0.50, 0.75];   // of the track
const HEADLIGHT_SECONDS = 26;
const HEADLIGHT_SEP = 0.010;           // the two lamps, in frame units
const HEADLIGHT_PX = 3.0;              // at 720 lines

const TIER_LAYOUT = {
  0: { antennas: 1, rings: 8, buffer: false },
  1: { antennas: 2, rings: 12, buffer: false },
  2: { antennas: 3, rings: 16, buffer: true }
};

const _size = new THREE.Vector2();
const _v3 = new THREE.Vector3();
const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}

// A small deterministic generator: the hill, the trees and the stars are the
// same every time the piece comes up, like a place one returns to.
function lcg(seed) {
  let s = seed >>> 0;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; };
}

// --- shaders -----------------------------------------------------------------

// A full-frame quad in clip space; uv in frame units through uAspect.
const screenVertexShader = `
  precision mediump float;
  uniform float uAspect;
  varying vec2 vFrame;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    vFrame = vec2(position.x * uAspect, position.y);
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

// The sky: a blue-black gradient, fine stars that twinkle IN TIME, and the
// glow of a city under the horizon on the right. Stars are one bright texel
// in a hashed grid - drawn at pixel scale through uPx so they stay points on
// any buffer size. HALF the stars, chosen by their hash, keep time: each
// draws its own rhythm - every beat, every second beat, every fourth, or the
// off-beat - and flashes at the top of its cycle then dies away over it, so
// the sky breathes with the tempo without ever blinking as one. The other
// half hold still, which is what makes the first half read as a rhythm and
// not as a flicker. uBeat is a continuous beat count (beats + phase); the
// highs deepen the flash.
const skyFragmentShader = `
  precision mediump float;
  uniform float uTime;
  uniform float uBeat;
  uniform float uHigh;
  uniform float uPx;
  uniform vec3 uCity;
  uniform float uCityX;
  uniform float uHorizon;
  varying vec2 vFrame;
  float hash21(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }
  void main() {
    vec2 f = vFrame;
    float up = clamp((f.y - uHorizon) / (1.0 - uHorizon), 0.0, 1.0);
    vec3 top = vec3(0.006, 0.010, 0.030);
    vec3 low = vec3(0.040, 0.055, 0.115);
    vec3 col = mix(low, top, pow(up, 0.65));
    // The city: warm, under the horizon on one side, fading up into the sky.
    float cx = exp(-pow((f.x - uCityX) / 0.75, 2.0));
    float cy = exp(-max(f.y - uHorizon, 0.0) * 5.5);
    col += uCity * cx * cy;
    // Stars: a cell every 0.014 units, one in eight lit, brightness and
    // twinkle rate from the hash, dimmed towards the horizon and by the city.
    vec2 cell = floor(f / 0.014);
    vec2 cp = (fract(f / 0.014) - 0.5) * 0.014;
    float h = hash21(cell);
    float h2 = hash21(cell + 17.0);
    vec2 jitter = (vec2(h2, hash21(cell + 31.0)) - 0.5) * 0.008;
    float d = length(cp - jitter);
    float star = step(h, 0.07) * (1.0 - smoothstep(0.0, uPx * 1.1, d));
    // The rhythm: h3 picks the period, h4 the offset within it.
    float h3 = hash21(cell + 53.0), h4 = hash21(cell + 71.0);
    float period = h3 < 0.45 ? 1.0 : (h3 < 0.75 ? 2.0 : (h3 < 0.9 ? 4.0 : 1.0));
    float offset = h3 < 0.9 ? floor(h4 * period) : 0.5;      // the last tenth on the off-beat
    float local = fract((uBeat - offset) / period);
    float flash = exp(-local * (4.0 + 6.0 * h2));            // sharp attack, decay over the cycle
    float depth = 0.55 + 0.45 * uHigh;                       // how far a star falls between flashes
    float tw = mix(1.0 - depth, 1.0, flash) * (0.7 + 0.5 * flash);
    float keepsTime = step(hash21(cell + 89.0), 0.5);       // one star in two, at random
    star *= mix(0.85, tw, keepsTime);
    star *= (0.35 + 0.65 * h2) * smoothstep(uHorizon, uHorizon + 0.25, f.y) * (1.0 - 0.8 * cx * cy);
    col += vec3(0.85, 0.9, 1.0) * star * 0.7;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// Tier 2: the rings' buffer composed on the sky, additively, with the
// crossings lifted.
const compositeFragmentShader = `
  precision mediump float;
  uniform sampler2D uBuffer;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uBuffer, vUv).rgb;
    float m = max(c.r, max(c.g, c.b));
    gl_FragColor = vec4(c * (1.0 + ${FRINGE_GAIN.toFixed(2)} * m), 1.0);
  }
`;

// Flat silhouettes (hill, trees) at a fixed z: black.
const flatVertexShader = `
  precision mediump float;
  void main() {
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const flatFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  void main() { gl_FragColor = vec4(uColor, 1.0); }
`;

// The beacon: a lamp and a halo, both from the filament's temperature. The
// lamp is blackbody-ish - dark red cold, orange-white hot - and never a
// white disc faded in and out.
const beaconVertexShader = `
  precision mediump float;
  varying vec2 vLocal;
  void main() {
    vLocal = uv * 2.0 - 1.0;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const beaconFragmentShader = `
  precision mediump float;
  uniform float uTemp;
  uniform float uLampFrac;
  varying vec2 vLocal;
  void main() {
    float d = length(vLocal);
    float t = uTemp;
    vec3 cold = vec3(0.35, 0.02, 0.0);
    vec3 hot = vec3(1.0, 0.32, 0.12);
    vec3 lampCol = mix(cold, hot, smoothstep(0.15, 1.0, t)) * smoothstep(0.02, 0.35, t);
    float lamp = 1.0 - smoothstep(uLampFrac * 0.6, uLampFrac, d);
    float halo = exp(-d * d * 6.0) * (1.0 - lamp) * 0.35 * t * t;
    gl_FragColor = vec4(lampCol * (lamp * 2.2) + hot * halo, 1.0);
  }
`;

// Headlights: two warm points, sized in device pixels.
const pointsVertexShader = `
  precision mediump float;
  uniform float uPointPx;
  void main() {
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = uPointPx;
  }
`;
const pointsFragmentShader = `
  precision mediump float;
  uniform float uGain;
  void main() {
    float d = length(gl_PointCoord - 0.5) * 2.0;
    float a = 1.0 - smoothstep(0.35, 1.0, d);
    gl_FragColor = vec4(vec3(1.0, 0.92, 0.72) * a * uGain, 1.0);
  }
`;

export class SignalVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER_LAYOUT[this._tier] || TIER_LAYOUT[1];
    const forced = parseInt(urlParam('signalantennas'), 10);
    this._antennaCap = Number.isFinite(forced) ? Math.max(1, Math.min(3, forced)) : this._layout.antennas;
    this._debugPulse = urlParam('signalpulse') === '1';
    this._debugCar = urlParam('signalcar') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._placeCamera();
    this._halfH = PLANE_D * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    this._aspect = camera.aspect || 16 / 9;
    this._time = 0;
    this._beatSeconds = 0.5;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null;
    this._freeBeatAt = 0; this._beatCount = 0;
    this._pendingDropMs = null; this._lastMode = null; this._lastDropAt = -1e9;
    this._forcePulse = false; this._debugPulseAt = 1.0;
    this._temp = 0;
    this._third = this._antennaCap >= 3 && urlParam('signalantennas') != null;   // the phrase antenna; forced on by the debug count
    this._speedMod = 1;
    this._lastProgress = null;
    this._car = -1;                         // -1 idle, else 0..1 along the road
    this._tint = new THREE.Color(0.8, 0.82, 0.85);
    this._objects = [];
    this._materials = [];
    this._geometries = [];

    this._buildSky();
    this._buildRings();
    this._buildHill();
    this._buildPylon();
    this._buildBeacon();
    this._layoutRings(true);
    this._applyPalette(null);
  }

  _placeCamera() {
    const c = this._camera;
    c.position.set(0, 0, 0);
    c.lookAt(0, 0, -PLANE_D);
    c.updateMatrixWorld();
  }

  // Frame units to world, on the picture's plane.
  _fx(x) { return x * this._halfH; }
  _fy(y) { return y * this._halfH; }

  // The hill's ridge, in frame units: a broad summit under the mast, a lower
  // flank either side, a little roughness so it reads as ground and not a
  // curve. Also the road's and the trees' floor.
  _hill(x) {
    const xp = this._pylonX();
    const u = x - xp;
    let y = HILL_FOOT_Y + (HILL_SUMMIT_Y - HILL_FOOT_Y) * Math.exp(-(u * u) / (2 * 0.62 * 0.62));
    y += 0.028 * Math.sin(2.7 * x + 1.3) + 0.014 * Math.sin(6.1 * x - 0.4) + 0.006 * Math.sin(13.0 * x + 2.0);
    // The summit is flat where the mast stands: a mast on a point is a spike.
    if (Math.abs(u) < 0.09) y = Math.max(y, this._summitY());
    return y;
  }
  _pylonX() { return (PYLON_X_FRAC * 2 - 1) * this._aspect; }
  _summitY() { return HILL_SUMMIT_Y + 0.028 * Math.sin(2.7 * this._pylonX() + 1.3); }

  // --- the sky and the rings' buffer ------------------------------------------
  _buildSky() {
    const geo = new THREE.PlaneGeometry(2, 2);
    this._skyUniforms = {
      uAspect: { value: this._aspect },
      uTime: { value: 0 },
      uBeat: { value: 0 },
      uHigh: { value: 0 },
      uPx: { value: 2 / 720 },
      uCity: { value: new THREE.Color(0.10, 0.06, 0.03) },
      uCityX: { value: this._aspect * 0.62 },
      uHorizon: { value: HILL_FOOT_Y + 0.05 }
    };
    const mat = new THREE.ShaderMaterial({
      uniforms: this._skyUniforms, vertexShader: screenVertexShader, fragmentShader: skyFragmentShader,
      depthTest: false, depthWrite: false
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = -1000;
    const self = this;
    // The sky is the first thing drawn each frame: the one place to read the
    // real buffer size, which the fat lines and the stars are sized in.
    mesh.onBeforeRender = function (renderer) { self._onFrame(renderer); };
    this._sky = mesh;
    this._add(mesh, geo, mat);

    if (this._layout.buffer) {
      this._buffer = new FeedbackBuffer({ scale: BUFFER_SCALE, single: true });
      this._ringScene = new THREE.Scene();
      this._compositeUniforms = { uAspect: this._skyUniforms.uAspect, uBuffer: { value: null } };
      const cmat = new THREE.ShaderMaterial({
        uniforms: this._compositeUniforms, vertexShader: screenVertexShader, fragmentShader: compositeFragmentShader,
        transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
      });
      const cgeo = new THREE.PlaneGeometry(2, 2);
      const cmesh = new THREE.Mesh(cgeo, cmat);
      cmesh.frustumCulled = false;
      cmesh.renderOrder = -900;
      this._composite = cmesh;
      this._add(cmesh, cgeo, cmat);
    }
  }

  _lineMaterial(widthPx, opacity, additive) {
    // dashed with an endless dash: no end extension, no round caps, so the
    // ninety-six segments of a ring abut instead of beading at every joint.
    // NO alpha-to-coverage: LineMaterial turns its edge softness into sample
    // coverage, which only works on a multisampled target. The rings' buffer
    // has one sample, and a line a pixel and a half wide came out DASHED -
    // every fragment whose edge alpha fell under a half was dropped.
    const mat = new LineMaterial({
      linewidth: widthPx + 2 * AA_PX, vertexColors: true, transparent: true, opacity: opacity,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      depthTest: false, depthWrite: false, dashed: true, dashSize: 1e9, gapSize: 0,
      alphaToCoverage: false
    });
    mat.resolution.set(1280, 720);
    // The analytic edge: vUv.x runs -1..1 across the drawn quad, so the
    // distance to the edge in pixels is (1 - |vUv.x|) x half the quad's
    // width; the alpha ramps over the last AA_PX of it.
    const halfPx = { value: (widthPx + 2 * AA_PX) / 2 };
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uHalfPx = halfPx;
      shader.fragmentShader = 'uniform float uHalfPx;\n' + shader.fragmentShader.replace(
        'gl_FragColor = vec4( diffuseColor.rgb, alpha );',
        'alpha *= clamp((1.0 - abs(vUv.x)) * uHalfPx / ' + AA_PX.toFixed(2) + ', 0.0, 1.0);\n\t\t\tgl_FragColor = vec4( diffuseColor.rgb, alpha );'
      );
    };
    mat.customProgramCacheKey = () => 'signal-aa';
    this._materials.push(mat);
    return mat;
  }

  _lineMesh(segments, mat, order) {
    const geo = new LineSegmentsGeometry();
    const pos = new Float32Array(segments * 6);
    const col = new Float32Array(segments * 6);
    geo.setPositions(pos);
    geo.setColors(col);
    geo.instanceCount = 0;
    const mesh = new LineSegments2(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    this._geometries.push(geo);
    return { mesh, geo, pos, col, posBuf: geo.attributes.instanceStart.data, colBuf: geo.attributes.instanceColorStart.data };
  }

  _buildRings() {
    const L = this._layout;
    const n = 3 * L.rings;                  // slots for every antenna the tier could ever show
    this._rings = [];
    for (let i = 0; i < n; i++) this._rings.push({ live: false, r: 0, age: 0, antenna: 0 });
    this._pulses = [];
    for (let i = 0; i < 3; i++) this._pulses.push({ live: false, r: 0, age: 0, antenna: 0 });
    const inBuffer = !!this._buffer;
    this._ringMat = this._lineMaterial(RING_WIDTH_PX, RING_OPACITY, true);
    this._ringMesh = this._lineMesh(n * RING_SEGMENTS, this._ringMat, -900);
    this._pulseMat = this._lineMaterial(PULSE_WIDTH_PX, 1.0, true);
    this._pulseMesh = this._lineMesh(3 * RING_SEGMENTS, this._pulseMat, -899);
    const host = inBuffer ? this._ringScene : this._scene;
    host.add(this._ringMesh.mesh);
    host.add(this._pulseMesh.mesh);
    if (!inBuffer) { this._objects.push(this._ringMesh.mesh, this._pulseMesh.mesh); }
    this._antennas = [new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2()];
    // The unit circle, once.
    this._circle = new Float32Array((RING_SEGMENTS + 1) * 2);
    for (let i = 0; i <= RING_SEGMENTS; i++) {
      const a = (i / RING_SEGMENTS) * Math.PI * 2;
      this._circle[i * 2] = Math.cos(a); this._circle[i * 2 + 1] = Math.sin(a);
    }
  }

  // --- the hill, the trees, the road -----------------------------------------
  _buildHill() {
    const A = this._aspect, H = this._halfH;
    // The silhouette: a strip from the ridge down past the frame's bottom.
    const cols = HILL_COLS;
    const pos = new Float32Array((cols + 1) * 2 * 3);
    const idx = [];
    for (let i = 0; i <= cols; i++) {
      const x = -A * OVERRUN + (2 * OVERRUN * A) * (i / cols);
      const y = this._hill(x);
      pos[i * 6] = x * H; pos[i * 6 + 1] = y * H; pos[i * 6 + 2] = -PLANE_D;
      pos[i * 6 + 3] = x * H; pos[i * 6 + 4] = -1.05 * H; pos[i * 6 + 5] = -PLANE_D;
      if (i < cols) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setIndex(idx);
    // transparent: true ON AN OPAQUE SILHOUETTE, deliberately. three draws
    // every opaque object before any transparent one, whatever their
    // renderOrders: an opaque hill went under the additive rings and the
    // rings crossed the ground. In the transparent queue the renderOrder
    // holds, and the hill, trees and mast cover the sky as intended.
    const mat = new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(0, 0, 0) } }, vertexShader: flatVertexShader, fragmentShader: flatFragmentShader,
      transparent: true, depthTest: false, depthWrite: false
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = -800;
    this._add(mesh, geo, mat);

    // The trees: conifers and a few round crowns along the ridge, never on
    // the flat summit, never under the mast.
    const rnd = lcg(0x5162);
    const tpos = [];
    const xp = this._pylonX();
    let placed = 0, tries = 0;
    while (placed < TREES && tries++ < 200) {
      const x = -A * 0.98 + rnd() * 1.96 * A;
      if (Math.abs(x - xp) < 0.16) continue;
      const y = this._hill(x) - 0.004;
      const h = 0.035 + rnd() * 0.045;
      const w = h * (0.32 + rnd() * 0.16);
      if (rnd() < 0.7) {
        // a conifer: a tall triangle and a short one over it
        tpos.push(x - w, y, x + w, y, x, y + h);
        tpos.push(x - w * 0.7, y + h * 0.45, x + w * 0.7, y + h * 0.45, x, y + h * 1.25);
      } else {
        // a broadleaf: a trunk and a rough crown
        const r = h * 0.55;
        tpos.push(x - w * 0.12, y, x + w * 0.12, y, x, y + h * 0.5);
        const cy = y + h * 0.55;
        const n = 7;
        for (let k = 0; k < n; k++) {
          const a0 = (k / n) * Math.PI * 2, a1 = ((k + 1) / n) * Math.PI * 2;
          const r0 = r * (0.85 + 0.3 * rnd()), r1 = r * (0.85 + 0.3 * rnd());
          tpos.push(x, cy, x + Math.cos(a0) * r0 * 1.2, cy + Math.sin(a0) * r0, x + Math.cos(a1) * r1 * 1.2, cy + Math.sin(a1) * r1);
        }
      }
      placed++;
    }
    const tp = new Float32Array(tpos.length / 2 * 3);
    for (let i = 0; i < tpos.length / 2; i++) { tp[i * 3] = tpos[i * 2] * H; tp[i * 3 + 1] = tpos[i * 2 + 1] * H; tp[i * 3 + 2] = -PLANE_D; }
    const tgeo = new THREE.BufferGeometry();
    tgeo.setAttribute('position', new THREE.BufferAttribute(tp, 3));
    const tmesh = new THREE.Mesh(tgeo, mat);
    tmesh.frustumCulled = false;
    tmesh.renderOrder = -780;
    this._objects.push(tmesh); this._geometries.push(tgeo);
    this._scene.add(tmesh);

    // The road: switchbacks up the front of the hill, from the right foot to
    // just under the summit. A shade above black - the eye finds it only
    // when the headlights trace it.
    const road = [];
    const legs = 5;
    const x0 = xp + 0.95 * Math.min(A - xp, 1.6), y0 = -1.0;
    const yTop = this._summitY() - 0.035;
    for (let i = 0; i <= legs; i++) {
      const u = i / legs;
      const y = y0 + (yTop - y0) * Math.pow(u, 0.85);
      const swing = (i % 2 === 0 ? 1 : -1) * (0.55 - 0.42 * u) * Math.min(1.0, A * 0.5);
      const x = i === legs ? xp + 0.05 : xp + 0.12 + swing;
      road.push(new THREE.Vector2(x, Math.min(y, this._hill(x) - 0.01)));
    }
    this._road = road;
    const rl = [];
    for (let i = 0; i < road.length - 1; i++) rl.push(road[i].x * H, road[i].y * H, -PLANE_D, road[i + 1].x * H, road[i + 1].y * H, -PLANE_D);
    const rgeo = new LineSegmentsGeometry();
    rgeo.setPositions(new Float32Array(rl));
    const rmat = new LineMaterial({ linewidth: 1.6, color: 0x0c0c0e, transparent: true, depthTest: false, depthWrite: false, dashed: true, dashSize: 1e9, gapSize: 0, alphaToCoverage: false });
    this._materials.push(rmat); this._geometries.push(rgeo);
    const rmesh = new LineSegments2(rgeo, rmat);
    rmesh.frustumCulled = false;
    rmesh.renderOrder = -790;
    this._roadMat = rmat;
    this._objects.push(rmesh);
    this._scene.add(rmesh);
    // Arc lengths, for the car.
    this._roadLen = [0];
    for (let i = 1; i < road.length; i++) this._roadLen.push(this._roadLen[i - 1] + road[i].distanceTo(road[i - 1]));

    // The headlights.
    const hgeo = new THREE.BufferGeometry();
    hgeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6), 3));
    this._headUniforms = { uPointPx: { value: HEADLIGHT_PX }, uGain: { value: 0 } };
    const hmat = new THREE.ShaderMaterial({
      uniforms: this._headUniforms, vertexShader: pointsVertexShader, fragmentShader: pointsFragmentShader,
      transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
    });
    const hpts = new THREE.Points(hgeo, hmat);
    hpts.frustumCulled = false;
    hpts.renderOrder = -770;
    hpts.visible = false;
    this._headlights = hpts;
    this._add(hpts, hgeo, hmat);
  }

  // --- the mast ---------------------------------------------------------------
  _buildPylon() {
    const H = this._halfH, A = this._aspect;
    const xp = this._pylonX(), yb = this._summitY();
    const top = yb + PYLON_H;
    const segs = [];   // [x0,y0,x1,y1, lit] in frame units; lit = how much the beacon reaches
    const lit = (y) => THREE.MathUtils.clamp((y - (top - PYLON_H * LATTICE_LIT_SPAN)) / (PYLON_H * LATTICE_LIT_SPAN), 0, 1);
    const hw = (y) => PYLON_BASE_HALF_W + (PYLON_TOP_HALF_W - PYLON_BASE_HALF_W) * ((y - yb) / PYLON_H);
    // Legs.
    segs.push([xp - hw(yb), yb, xp - hw(top), top]);
    segs.push([xp + hw(yb), yb, xp + hw(top), top]);
    // Panels: a brace and an X per panel, the lower panels taller.
    let y = yb;
    for (let i = 0; i < PYLON_PANELS; i++) {
      const u0 = i / PYLON_PANELS, u1 = (i + 1) / PYLON_PANELS;
      const y0 = yb + PYLON_H * Math.pow(u0, 1.15), y1 = yb + PYLON_H * Math.pow(u1, 1.15);
      segs.push([xp - hw(y1), y1, xp + hw(y1), y1]);
      segs.push([xp - hw(y0), y0, xp + hw(y1), y1]);
      segs.push([xp + hw(y0), y0, xp - hw(y1), y1]);
      y = y1;
    }
    // A platform two thirds up, for the silhouette.
    const yP = top - 0.17;
    segs.push([xp - hw(yP) * 1.6, yP, xp + hw(yP) * 1.6, yP]);
    // The mast, the crossbar and the antennas: two at the crossbar's ends, 3%
    // of the width apart, and the third (the phrase antenna) on the mast's
    // tip between them, a little higher. Every ring is born on one of these.
    const yM = top + MAST_H;
    segs.push([xp, top, xp, yM]);
    const dx = ANTENNA_SEP_FRAC * A;    // half the separation: 3% of the width between them
    segs.push([xp - dx - 0.006, yM - 0.02, xp + dx + 0.006, yM - 0.02]);
    segs.push([xp - dx, yM - 0.02, xp - dx, yM - 0.02 + ANTENNA_STUB]);
    segs.push([xp + dx, yM - 0.02, xp + dx, yM - 0.02 + ANTENNA_STUB]);
    segs.push([xp, yM, xp, yM + ANTENNA_STUB * 0.7]);
    // Guy lines, two each side to the ground. Never lit: a cable's whole
    // length interpolating from a lit anchor turned the mast into a red tent.
    const guyFrom = segs.length;
    for (const s of [-1, 1]) {
      segs.push([xp + s * hw(top), top, xp + s * 0.42, this._hill(xp + s * 0.42)]);
      segs.push([xp + s * hw(yP), yP, xp + s * 0.30, this._hill(xp + s * 0.30)]);
    }
    this._antennaPos = [
      new THREE.Vector2(xp - dx, yM - 0.02 + ANTENNA_STUB),
      new THREE.Vector2(xp + dx, yM - 0.02 + ANTENNA_STUB),
      new THREE.Vector2(xp, yM + ANTENNA_STUB * 0.7)
    ];
    this._beaconPos = new THREE.Vector2(xp, yM + ANTENNA_STUB * 0.7 + 0.008);

    const n = segs.length;
    const pos = new Float32Array(n * 6), col = new Float32Array(n * 6);
    this._pylonLit = new Float32Array(n * 2);
    for (let i = 0; i < n; i++) {
      const s = segs[i];
      pos[i * 6] = s[0] * H; pos[i * 6 + 1] = s[1] * H; pos[i * 6 + 2] = -PLANE_D;
      pos[i * 6 + 3] = s[2] * H; pos[i * 6 + 4] = s[3] * H; pos[i * 6 + 5] = -PLANE_D;
      const guy = i >= guyFrom;
      this._pylonLit[i * 2] = guy ? 0 : lit(s[1]); this._pylonLit[i * 2 + 1] = guy ? 0 : lit(s[3]);
    }
    const geo = new LineSegmentsGeometry();
    geo.setPositions(pos);
    geo.setColors(col);
    this._geometries.push(geo);
    this._pylonCol = col;
    this._pylonColBuf = geo.attributes.instanceColorStart.data;
    const mat = new LineMaterial({ linewidth: 1.6, vertexColors: true, transparent: true, depthTest: false, depthWrite: false, dashed: true, dashSize: 1e9, gapSize: 0, alphaToCoverage: false });
    mat.resolution.set(1280, 720);
    this._materials.push(mat);
    this._pylonMat = mat;
    const mesh = new LineSegments2(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = -700;
    this._objects.push(mesh);
    this._scene.add(mesh);
    this._writePylonColours(0);
  }

  _writePylonColours(temp) {
    const col = this._pylonCol, lit = this._pylonLit;
    const n = col.length / 6;
    // The lattice is a silhouette, but a lit filament reaches its top: warm
    // red on the upper segments, scaled by the temperature.
    const g = LATTICE_LIT_GAIN * temp * temp;
    for (let i = 0; i < n; i++) {
      const a = lit[i * 2] * g, b = lit[i * 2 + 1] * g;
      col[i * 6] = a; col[i * 6 + 1] = a * 0.18; col[i * 6 + 2] = a * 0.05;
      col[i * 6 + 3] = b; col[i * 6 + 4] = b * 0.18; col[i * 6 + 5] = b * 0.05;
    }
    this._pylonColBuf.needsUpdate = true;
  }

  _buildBeacon() {
    const H = this._halfH;
    const size = BEACON_HALO_R * 2 * H;
    const geo = new THREE.PlaneGeometry(size, size);
    this._beaconUniforms = { uTemp: { value: 0 }, uLampFrac: { value: BEACON_R / BEACON_HALO_R } };
    const mat = new THREE.ShaderMaterial({
      uniforms: this._beaconUniforms, vertexShader: beaconVertexShader, fragmentShader: beaconFragmentShader,
      transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(this._beaconPos.x * H, this._beaconPos.y * H, -PLANE_D);
    mesh.frustumCulled = false;
    mesh.renderOrder = -690;
    this._add(mesh, geo, mat);
  }

  _add(mesh, geo, mat) {
    this._objects.push(mesh);
    if (geo) this._geometries.push(geo);
    if (mat) this._materials.push(mat);
    this._scene.add(mesh);
  }

  // --- per frame, from the renderer ------------------------------------------
  _onFrame(renderer) {
    renderer.getDrawingBufferSize(_size);
    const w = _size.x, h = _size.y;
    this._skyUniforms.uPx.value = 2 / Math.max(1, h);
    this._headUniforms.uPointPx.value = HEADLIGHT_PX * Math.max(0.6, h / 720);
    this._pylonMat.resolution.set(w, h);
    this._roadMat.resolution.set(w, h);
    this._renderer = renderer;
    if (this._buffer) {
      this._buffer.ensureSize(renderer);
      this._ringMat.resolution.set(this._buffer.width, this._buffer.height);
      this._pulseMat.resolution.set(this._buffer.width, this._buffer.height);
    } else {
      this._ringMat.resolution.set(w, h);
      this._pulseMat.resolution.set(w, h);
    }
  }

  // Tier 2: the rings into their own target, from update() - OUTSIDE the
  // main render. Rendered nested from the sky's onBeforeRender, one segment
  // in two of every ring went missing (the instanced fat lines do not
  // survive a render call inside a render call); drawn here, before the
  // frame, they are whole. The first frame has no renderer yet and skips.
  _renderBuffer() {
    const renderer = this._renderer;
    if (!renderer || !this._buffer) return;
    this._buffer.ensureSize(renderer);
    const prev = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.setRenderTarget(this._buffer.write);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, false, false);
    renderer.autoClear = false;
    renderer.render(this._ringScene, this._camera);
    renderer.autoClear = prevAutoClear;
    renderer.setRenderTarget(prev);
    this._compositeUniforms.uBuffer.value = this._buffer.write.texture;
  }

  // The frame's aspect can change (a resize, or the harness): the plane's
  // width follows, so the mast stays at 35% and the city keeps its side.
  _checkAspect() {
    const a = this._camera.aspect || 16 / 9;
    if (Math.abs(a - this._aspect) < 1e-4) return;
    this._aspect = a;
    this._skyUniforms.uAspect.value = a;
    this._skyUniforms.uCityX.value = a * 0.62;
    // The geometry is built for one aspect; rebuild it.
    this._disposeObjects();
    const camera = this._camera, scene = this._scene;
    const keep = { rings: this._rings, pulses: this._pulses, temp: this._temp, third: this._third, car: this._car, tint: this._tint.clone() };
    this._objects = []; this._materials = []; this._geometries = [];
    if (this._buffer) { this._buffer.dispose(); this._buffer = null; }
    this._scene = scene; this._camera = camera;
    this._buildSky(); this._buildRings(); this._buildHill(); this._buildPylon(); this._buildBeacon();
    this._temp = keep.temp; this._third = keep.third; this._car = keep.car; this._tint.copy(keep.tint);
    for (let i = 0; i < Math.min(keep.rings.length, this._rings.length); i++) Object.assign(this._rings[i], keep.rings[i]);
    this._layoutRings(true);
    this._applyPalette(this._lastProfile);
  }

  _layoutRings(force) {
    for (let i = 0; i < 3; i++) this._antennas[i].copy(this._antennaPos[i]);
  }

  _activeAntennas() {
    const n = Math.min(this._antennaCap, 3);
    if (n >= 3) return this._third ? 3 : 2;
    return n;
  }

  // A ring's reach: the far edge of the frame from its antenna, on the
  // sky's side. The expansion speed is set so that distance takes six beats.
  _reach(antenna) {
    const a = this._antennas[antenna];
    return Math.max(this._aspect - a.x, a.x + this._aspect);
  }

  _spawnRing(antenna) {
    const L = this._layout;
    let slot = null, oldest = null;
    for (const r of this._rings) {
      if (!r.live) { slot = r; break; }
      if (r.antenna === antenna && (!oldest || r.age > oldest.age)) oldest = r;
    }
    // Sixteen a tier's antenna at most: past that the oldest one gives way.
    let count = 0;
    for (const r of this._rings) if (r.live && r.antenna === antenna) count++;
    if (count >= L.rings) slot = oldest;
    if (!slot) return;
    slot.live = true; slot.r = 0; slot.age = 0; slot.antenna = antenna;
  }

  // The drop: from the two outer antennas at once, never the third - three
  // near-coincident six-pixel rings summed through the bloom were a blast,
  // not a pulse.
  _firePulse() {
    const n = Math.min(2, this._activeAntennas());
    for (let i = 0; i < 3; i++) {
      const p = this._pulses[i];
      p.live = i < n; p.r = 0; p.age = 0; p.antenna = i;
    }
  }

  // Safe-zone attenuation, per vertex, on the CPU: the rings are rewritten
  // every frame anyway, and LineMaterial has no vertex chunk to hang the
  // shared one on. Same rounded-box distance in the same height units.
  _safe(x, y) {
    const c = safeZoneUniforms.uSafeCenter.value, hs = safeZoneUniforms.uSafeHalfSize.value, f = safeZoneUniforms.uSafeFeather.value;
    const qx = Math.abs(x - c.x) - hs.x, qy = Math.abs(y - c.y) - hs.y;
    const mx = Math.max(qx, 0), my = Math.max(qy, 0);
    const d = Math.sqrt(mx * mx + my * my) + Math.min(Math.max(qx, qy), 0);
    const t = THREE.MathUtils.clamp(d / Math.max(f, 1e-4), 0, 1);
    return SAFE_FLOOR + (1 - SAFE_FLOOR) * t * t * (3 - 2 * t);
  }

  // Every live ring as ninety-six segments, colour = tint x fade x safe zone.
  // Segments entirely off the frame are skipped, and everything under the
  // ridge is covered by the hill drawn after.
  _writeRings(mesh, list, life, gain) {
    const H = this._halfH, A = this._aspect;
    const pos = mesh.pos, col = mesh.col, circ = this._circle;
    const tint = this._tint;
    let w = 0;
    for (const ring of list) {
      if (!ring.live) continue;
      const a = this._antennas[ring.antenna];
      const u = ring.age / life;
      const fade = Math.max(0, 1 - u);     // linear: 0.22 at birth, 0 at the eighth beat
      const g = fade * gain;
      if (g <= 0.001) continue;
      const r = ring.r;
      for (let i = 0; i < RING_SEGMENTS; i++) {
        const x0 = a.x + circ[i * 2] * r, y0 = a.y + circ[i * 2 + 1] * r;
        const x1 = a.x + circ[i * 2 + 2] * r, y1 = a.y + circ[i * 2 + 3] * r;
        if ((y0 < HILL_FOOT_Y - 0.08 && y1 < HILL_FOOT_Y - 0.08) || (y0 > OVERRUN && y1 > OVERRUN)) continue;
        if ((x0 < -A * OVERRUN && x1 < -A * OVERRUN) || (x0 > A * OVERRUN && x1 > A * OVERRUN)) continue;
        const s0 = this._safe(x0, y0) * g, s1 = this._safe(x1, y1) * g;
        const p = w * 6;
        pos[p] = x0 * H; pos[p + 1] = y0 * H; pos[p + 2] = -PLANE_D;
        pos[p + 3] = x1 * H; pos[p + 4] = y1 * H; pos[p + 5] = -PLANE_D;
        col[p] = tint.r * s0; col[p + 1] = tint.g * s0; col[p + 2] = tint.b * s0;
        col[p + 3] = tint.r * s1; col[p + 4] = tint.g * s1; col[p + 5] = tint.b * s1;
        w++;
      }
    }
    mesh.geo.instanceCount = w;
    mesh.posBuf.needsUpdate = true;
    mesh.colBuf.needsUpdate = true;
  }

  _applyPalette(profile) {
    const palette = profile && profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const src = vivid.length ? vivid[0] : (dominant.length ? dominant[0] : null);
    if (src) _col.set(src); else _col.setRGB(0.55, 0.65, 0.9);
    _col.getHSL(_hsl);
    // The rings: the palette's hue, half its saturation, nearly white.
    this._tint.setHSL(_hsl.h, RING_TINT_SAT, RING_TINT_LIGHT);
    // The city: the same hue pulled towards sodium, dim.
    const cityHue = _hsl.h * 0.35 + 0.075 * 0.65;
    this._skyUniforms.uCity.value.setHSL(cityHue, 0.65, 0.5).multiplyScalar(0.16);
  }

  onTrackChange(profile) {
    this._applyPalette(profile);
    this._lastProgress = null;
  }

  update(deltaTime, profile) {
    this._lastProfile = profile;
    this._checkAspect();
    this._placeCamera();
    const director = profile.director || {};
    const paused = profile.playbackState !== 'PLAYING';
    if (profile.artworkPalette && profile.artworkPalette !== this._lastPalette) {
      this._lastPalette = profile.artworkPalette;
      this._applyPalette(profile);
    }
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 60, 180) : 110;
    this._beatSeconds = 60 / bpm;
    const beat = this._beatSeconds;
    const bands = director.bands || { low: 0, high: 0 };
    const low = bands.low || 0, high = bands.high || 0;
    const progress = profile.progress || 0;

    if (!paused) {
      const dt = Math.min(deltaTime, 0.05);
      this._time += dt;
      const t = this._time;

      // --- the clocks
      let beatEdge = false, phraseEdge = false;
      if (profile.beatPhase != null) {
        if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
        this._lastBeatPhase = profile.beatPhase;
      } else if (t - this._freeBeatAt >= beat) { this._freeBeatAt = t; beatEdge = true; }
      if (beatEdge) this._beatCount++;
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
      this._lastPhrase = phrase;
      // The bar: the beacon is lit for the first half beat of it.
      let barPhase;
      if (profile.barPhase != null) barPhase = profile.barPhase;
      else barPhase = ((this._beatCount % 4) + (profile.beatPhase != null ? profile.beatPhase : (t - this._freeBeatAt) / beat)) / 4;

      // --- the drop, on the frame that crosses the mark
      const ev = director.nextEvent;
      let dropNow = false;
      if (ev && ev.type === 'drop' && ev.msUntil != null) {
        if (ev.msUntil <= dt * 500 + 30) dropNow = true;
        if (this._pendingDropMs != null && this._pendingDropMs < 400 && ev.msUntil > this._pendingDropMs + 250) dropNow = true;
        this._pendingDropMs = ev.msUntil;
      } else {
        if (this._pendingDropMs != null && this._pendingDropMs < 400) dropNow = true;
        this._pendingDropMs = null;
      }
      if (director.hasReliableGrid && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
      this._lastMode = director.mode;
      if (this._debugPulse && t >= this._debugPulseAt) { this._forcePulse = true; this._debugPulseAt = t + 8 * beat; }
      if ((dropNow && t - this._lastDropAt > 2.0) || this._forcePulse) {
        this._forcePulse = false; this._lastDropAt = t; this._firePulse();
      }

      // --- the phrase: the third antenna comes or goes
      if (phraseEdge && this._antennaCap >= 3) this._third = !this._third;

      // --- the beat: a ring from every active antenna
      if (beatEdge) {
        const n = this._activeAntennas();
        for (let i = 0; i < n; i++) this._spawnRing(i);
      }

      // --- the rings expand at one speed, swayed by the lows, and age in beats
      // Without an analysis the bands sit at zero, which would read as a
      // permanent -15%: the sway only applies to a measured low band.
      const sway = director.hasReliableGrid ? 1 + RING_LOW_SWAY * (low * 2 - 1) : 1;
      this._speedMod += (sway - this._speedMod) * Math.min(1, dt * 4);
      const ageStep = dt / beat;
      for (const r of this._rings) {
        if (!r.live) continue;
        r.r += (this._reach(r.antenna) / (RING_REACH_BEATS * beat)) * this._speedMod * dt;
        r.age += ageStep;
        if (r.age >= RING_LIFE_BEATS) r.live = false;
      }
      for (const p of this._pulses) {
        if (!p.live) continue;
        p.r += (this._reach(p.antenna) / (PULSE_BEATS * beat)) * dt;
        p.age += ageStep;
        if (p.age >= PULSE_BEATS * 1.15) p.live = false;
      }

      // --- the beacon: a filament, heated on the bar
      const on = barPhase < BEACON_ON_BARS ? 1 : 0;
      this._temp += (on - this._temp) * Math.min(1, (on > this._temp ? HEAT_RATE : COOL_RATE) * dt);

      // --- the road: a car climbs at a quarter, a half, three quarters
      if (this._lastProgress != null) {
        for (const p of HEADLIGHT_PASSES) {
          if (this._lastProgress < p && progress >= p && this._car < 0) this._car = 0;
        }
      }
      this._lastProgress = progress;
      if (this._debugCar && this._car < 0) this._car = 0;
      if (this._car >= 0) {
        this._car += dt / HEADLIGHT_SECONDS;
        if (this._car >= 1) this._car = -1;
      }
    }

    // --- uniforms and buffers
    this._skyUniforms.uTime.value = this._time;
    // A continuous beat count for the stars: measured phase when there is
    // one, the free-running clock otherwise.
    this._skyUniforms.uBeat.value = this._beatCount + (profile.beatPhase != null ? profile.beatPhase : Math.min(1, (this._time - this._freeBeatAt) / beat));
    this._skyUniforms.uHigh.value = high;
    this._beaconUniforms.uTemp.value = this._temp;
    this._writePylonColours(this._temp);
    this._writeRings(this._ringMesh, this._rings, RING_LIFE_BEATS, 1.0);
    this._writeRings(this._pulseMesh, this._pulses, PULSE_BEATS, PULSE_GAIN);
    this._writeHeadlights();
    this._renderBuffer();
  }

  _writeHeadlights() {
    const pts = this._headlights;
    if (this._car < 0) { pts.visible = false; return; }
    const road = this._road, len = this._roadLen;
    const total = len[len.length - 1];
    // Slow on the bends, like a real climb: ease the arc length.
    const s = total * (this._car < 0.5 ? 2 * this._car * this._car : 1 - Math.pow(-2 * this._car + 2, 2) / 2) * 0.999;
    let i = 1;
    while (i < len.length - 1 && len[i] < s) i++;
    const a = road[i - 1], b = road[i];
    const u = (s - len[i - 1]) / Math.max(1e-6, len[i] - len[i - 1]);
    const x = a.x + (b.x - a.x) * u, y = a.y + (b.y - a.y) * u;
    const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1;
    // Two lamps side by side, across the road's direction.
    const nx = -dy / L * HEADLIGHT_SEP, ny = dx / L * HEADLIGHT_SEP;
    const H = this._halfH;
    const arr = pts.geometry.attributes.position.array;
    arr[0] = (x + nx) * H; arr[1] = (y + ny) * H; arr[2] = -PLANE_D;
    arr[3] = (x - nx) * H; arr[4] = (y - ny) * H; arr[5] = -PLANE_D;
    pts.geometry.attributes.position.needsUpdate = true;
    // Faint at the foot, brighter as the car turns towards us on the last legs.
    this._headUniforms.uGain.value = 0.6 + 0.4 * this._car;
    pts.visible = true;
  }

  _disposeObjects() {
    for (const o of this._objects) if (o.parent) o.parent.remove(o);
    if (this._ringScene) {
      if (this._ringMesh) this._ringScene.remove(this._ringMesh.mesh);
      if (this._pulseMesh) this._ringScene.remove(this._pulseMesh.mesh);
    }
    for (const g of this._geometries) g.dispose();
    for (const m of this._materials) m.dispose();
    this._objects = []; this._geometries = []; this._materials = [];
  }

  dispose() {
    this._disposeObjects();
    if (this._buffer) { this._buffer.dispose(); this._buffer = null; }
    this._ringScene = null;
    this._scene = null;
  }
}
